/*
 * lowest-price-everywhere.js
 * Shows the LOWEST available variant price on every product card
 * (collections, search, home, recommendations, related products).
 *
 * Product pages are handled by variant-picker.liquid — this script
 * skips the main product there but still patches recommendation cards.
 */
(function () {
  'use strict';

  var VERSION = 'v1.0';
  if (window.__lowestPriceEverywhereInit === VERSION) return;
  window.__lowestPriceEverywhereInit = VERSION;

  // ─── Detect page context ───
  var isProductPage = /^\/products\//.test(window.location.pathname);
  var mainPicker = null;
  var mainProductId = null;

  if (isProductPage) {
    // Find the MAIN product's variant picker (first one in the DOM)
    mainPicker = document.querySelector('[data-variant-picker]');
    if (mainPicker) {
      mainProductId = mainPicker.getAttribute('data-product-id');
    }
  }

  // ─── Currency detection ───
  var currencySymbol = '£';
  try {
    var symMap = { GBP: '£', USD: '$', EUR: '€', JPY: '¥', INR: '₹', AUD: 'A$', CAD: 'C$' };
    if (window.Shopify && window.Shopify.currency && symMap[window.Shopify.currency.active]) {
      currencySymbol = symMap[window.Shopify.currency.active];
    }
    var sample = document.querySelector('.pp-block .price-item, .price .price-item');
    if (sample) {
      var m = (sample.textContent || '').match(/([£$€¥₹])/);
      if (m) currencySymbol = m[1];
    }
  } catch (e) {}

  function formatPrice(cents) {
    var n = parseInt(cents, 10);
    if (isNaN(n)) return currencySymbol + '0.00';
    return currencySymbol + (n / 100).toFixed(2);
  }

  // ─── Cache ───
  var productCache = {};
  var pendingFetches = {};

  // ─── Find the lowest available variant ───
  function findLowestAvailableVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    var pool = product.variants.filter(function (v) { return v.available; });
    if (pool.length === 0) pool = product.variants;
    return pool.reduce(function (a, b) {
      return parseInt(a.price, 10) <= parseInt(b.price, 10) ? a : b;
    });
  }

  // ─── Patch a single price block ───
  function patchPriceBlock(block, product) {
    var lowest = findLowestAvailableVariant(product);
    if (!lowest) return;

    var priceCents = parseInt(lowest.price, 10);
    var compareCents = lowest.compare_at_price ? parseInt(lowest.compare_at_price, 10) : 0;
    var isSale = compareCents > priceCents;

    var priceStr = formatPrice(priceCents);
    var compareStr = isSale ? formatPrice(compareCents) : null;

    var saleEls = block.querySelectorAll('.price-item--sale');
    var regularEls = block.querySelectorAll('.price-item--regular');
    var hasSale = saleEls.length > 0;

    if (isSale) {
      // ── Sale state ──
      if (hasSale) {
        saleEls.forEach(function (el) {
          if (el.textContent.trim() !== priceStr) el.textContent = priceStr;
          el.style.display = '';
        });
        regularEls.forEach(function (el) {
          if (el.textContent.trim() !== compareStr) el.textContent = compareStr;
          el.style.display = '';
        });
      } else {
        // No sale element exists — show price only
        regularEls.forEach(function (el) {
          if (el.textContent.trim() !== priceStr) el.textContent = priceStr;
          el.style.display = '';
        });
      }
      var container = block.querySelector('.price');
      if (container) container.classList.add('price--on-sale');
    } else {
      // ── Non-sale state ──
      if (hasSale) {
        // Hide the sale element; regular becomes current price
        saleEls.forEach(function (el) { el.style.display = 'none'; });
        regularEls.forEach(function (el) {
          if (el.textContent.trim() !== priceStr) el.textContent = priceStr;
          el.style.display = '';
          el.style.textDecoration = 'none';
        });
      } else {
        regularEls.forEach(function (el) {
          if (el.textContent.trim() !== priceStr) el.textContent = priceStr;
        });
      }
      var container2 = block.querySelector('.price');
      if (container2) container2.classList.remove('price--on-sale');
    }

    // Fallback — text-match any leftover currency leaf
    if (saleEls.length === 0 && regularEls.length === 0) {
      var regex = /^[£$€¥₹]\s?[\d,]+\.\d{2}$/;
      block.querySelectorAll('.money, .price-item, [class*="price"] span').forEach(function (el) {
        if (el.children.length > 0) return;
        if (el.closest('[data-variant-picker]')) return;
        var text = (el.textContent || '').trim();
        if (regex.test(text)) el.textContent = priceStr;
      });
    }
  }

  // ─── Reveal the price block (adds .price-ready) ───
  function revealBlock(block) {
    if (block.classList.contains('price-ready')) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        block.classList.add('price-ready');
      });
    });
  }

  // ─── Skip main product on product pages ───
  function shouldSkip(block) {
    if (!mainProductId) return false;
    var blockProductId = block.getAttribute('data-product-id');
    return blockProductId === mainProductId;
  }

  // ─── Process one block ───
  function processBlock(block) {
    if (block.dataset.lowestPricePatched === VERSION) return;
    block.dataset.lowestPricePatched = VERSION;

    // Always reveal — safety net so price never stays hidden
    revealBlock(block);

    // Skip main product price (variant-picker handles it)
    if (shouldSkip(block)) return;

    var handle = block.getAttribute('data-product-handle');
    if (!handle) {
      var card = block.closest('[data-product-handle]');
      if (card) handle = card.getAttribute('data-product-handle');
    }
    if (!handle) return;

    // Cached — patch now
    if (productCache[handle]) {
      patchPriceBlock(block, productCache[handle]);
      return;
    }

    // Fetch in parallel (deduped)
    if (!pendingFetches[handle]) {
      pendingFetches[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          delete pendingFetches[handle];
          if (!product) return null;
          productCache[handle] = product;

          // Patch every block for this handle
          document.querySelectorAll('.pp-block').forEach(function (b) {
            if (b.getAttribute('data-product-handle') === handle && !shouldSkip(b)) {
              patchPriceBlock(b, product);
            }
          });
          return product;
        })
        .catch(function (err) {
          delete pendingFetches[handle];
          block.dataset.lowestPricePatched = ''; // allow retry
          console.warn('[lowest-price] fetch failed:', handle, err);
          return null;
        });
    }
  }

  // ─── Init — process all blocks ───
  function init() {
    var blocks = document.querySelectorAll('.pp-block[data-product-handle]');
    if (!blocks.length) return;

    // Stagger to avoid network spikes on large grids
    blocks.forEach(function (block, i) {
      setTimeout(function () { processBlock(block); }, Math.min(i * 40, 1200));
    });
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on section reload (filters, pagination)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('facets:updated', init);

  // Watch for dynamically-added cards (sort/filter updates)
  try {
    var observer = new MutationObserver(function (mutations) {
      var needsInit = false;
      mutations.forEach(function (m) {
        if (!m.addedNodes) return;
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches('.pp-block[data-product-handle]')) needsInit = true;
          if (n.querySelector && n.querySelector('.pp-block[data-product-handle]')) needsInit = true;
        });
      });
      if (needsInit) init();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}

  // Debug helper — type `__lowestPrice.cache` in console to inspect
  window.__lowestPrice = {
    version: VERSION,
    cache: productCache,
    refresh: init
  };
})();